import React, { useEffect, useState } from "react";
import { Accordion, Button, Image, Modal, Offcanvas } from "react-bootstrap";
import { useProductDetails, useProductMapping } from "modules/dashboards/actionables/store";
import { orderedProductDetails } from "modules/utilities/utility";
import ReactPlayer from "react-player";
import SoftBadge from "modules/common/SoftBadge";
import { startCase } from "lodash";
import { Ayurveda } from "assets/svgs/ayurveda";
import { Allopathy } from "assets/svgs/allopathy";
import { PlayIcon } from "assets/svgs/plaicon";
import AdditionImage from "./AdditionImage";
import { CDN_BASE_URL } from "../../../config/env";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import RevampProductsButton from "modules/revamp_products/components/RevampProductsButton";

const ProductDetails = () => {
    const { productsData, fetchProducts } = useProductMapping();
    const [productToMap, setProductToMap] = useState([]);
    const [show, setShow] = useState(false);

    useEffect(() => {
        fetchProducts();
    }, []);

    useEffect(() => {
        if (Array.isArray(productsData) && productsData.length > 0) {
            const filteredProducts = orderedProductDetails(productsData);
            setProductToMap(filteredProducts);
        }
    }, [productsData]);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <button onClick={handleShow} className="topbar-pill topbar-pill--products">
                <span className="topbar-pill__icon">
                    <FontAwesomeIcon icon="capsules" />
                </span>
                <span>Products</span>
            </button>

            <RevampProductsButton />

            <Offcanvas
                show={show}
                style={{ width: "90vw", overflowY: "auto" }}
                onHide={handleClose}
                placement="end"
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>
                        <h5>Products</h5>
                    </Offcanvas.Title>
                </Offcanvas.Header>

                <Offcanvas.Body>
                    <div className="product-grid-container">
                        {productToMap.length > 0 &&
                            productToMap.map((product, index) => (
                                <Product key={product.product_principal_id || index} product={product} />
                            ))}
                    </div>

                    <div>
                        <AdditionImage />
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </div>
    );
};

export default ProductDetails;

function Product({ product }) {
    const [modalShow, setModalShow] = useState(false);
    const [productInfo, setProductInfo] = useState(null);
    const { productDetails, fetchProductDetails } = useProductDetails();

    useEffect(() => {
        setProductInfo(productDetails);
    }, [productDetails]);

    return (
        <>
            <div
                className="d-flex border rounded"
                onClick={() => {
                    fetchProductDetails(product.product_principal_id);
                    setModalShow(true);
                }}
            >
                <Image
                    src={`${CDN_BASE_URL}${product.cart_cdn_images}`}
                    height="120px"
                    width="120px"
                />
                <div className="d-flex flex-column justify-content-center">
                    <h5>{product.medicine.display_name}</h5>
                    {product.medicine.description}
                    <p>Dosage: {product?.medicine?.dosage}</p>
                </div>
            </div>

            <Modal
                show={modalShow}
                onHide={() => setModalShow(false)}
                size="md"
                aria-labelledby="contained-modal-title-vcenter"
                top
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        {product.medicine.display_name}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {productInfo && (
                        <div>
                            <div className="px-4">
                                <div className="d-flex border-bottom align-items-center border-2">
                                    <div className="px-4">
                                        <Image
                                            src={productInfo?.product_image}
                                            height="150px"
                                            width="160px"
                                        />
                                    </div>
                                    <div className="p-2">
                                        <h3>{productInfo?.product_name}</h3>
                                        {productInfo?.type && (
                                            <SoftBadge bg="info" className="me-2">
                                                {productInfo?.type?.toLowerCase() === "allopathy" ? (
                                                    <Allopathy />
                                                ) : (
                                                    <Ayurveda />
                                                )}
                                                {startCase(productInfo?.type)}
                                            </SoftBadge>
                                        )}
                                        <div className="flex justify-start mt-2 ml-2">
                                            {productInfo?.course_duration && (
                                                <SoftBadge bg="info" className="me-2">
                                                    {startCase(`${productInfo?.course_duration} course`)}
                                                </SoftBadge>
                                            )}
                                            <SoftBadge bg="info" className="me-2">
                                                {productInfo?.dosage}
                                            </SoftBadge>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div
                                className="scrollable-container mt-2 px-4"
                                style={{
                                    height: "60vh",
                                    overflowY: "scroll",
                                    overflowX: "hidden",
                                }}
                            >
                                <div className="mt-1">
                                    <h4>{productInfo?.doctor_recommendation?.title}</h4>
                                    <p>{productInfo?.doctor_recommendation?.description}</p>
                                </div>

                                <div className="mt-2">
                                    <h4>{productInfo?.how_it_works?.title}</h4>
                                    <p>{productInfo?.how_it_works?.description}</p>
                                </div>

                                <div className="mt-4">
                                    <ReactPlayer
                                        url={productInfo?.how_to_use?.media?.[0]?.desktop_url}
                                        controls
                                        playing
                                        width="450px"
                                        height="245px"
                                        light={
                                            <Image
                                                src={productInfo?.how_to_use?.media?.[0]?.thumbnail}
                                                height="245px"
                                                width="450px"
                                            />
                                        }
                                        playIcon={<PlayIcon />}
                                        style={{ borderRadius: "4px" }}
                                    />
                                </div>

                                <div className="mt-3 mx-2">
                                    <h4>{productInfo?.how_to_use?.title}</h4>
                                    <p>{productInfo?.how_to_use?.description}</p>
                                </div>

                                <div className="mt-4">
                                    <Image
                                        src={productInfo?.key_ingredients?.image_url}
                                        alt="ingredients"
                                        height="340px"
                                        width="440px"
                                    />
                                </div>

                                <div>
                                    <h4 className="my-4">{productInfo?.faq?.title} :</h4>
                                    <Accordion>
                                        {productInfo?.faq?.faq_list?.map((faq, index) => (
                                            <Accordion.Item
                                                key={`faq-${index}`}
                                                eventKey={String(index)}
                                            >
                                                <Accordion.Header>{faq?.title}</Accordion.Header>
                                                <Accordion.Body>{faq?.description}</Accordion.Body>
                                            </Accordion.Item>
                                        ))}
                                    </Accordion>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button onClick={() => setModalShow(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
